import { unref as _unref, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, Transition as _Transition, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex flex-col justify-between h-full" }
const _hoisted_2 = { class: "px-6 md:px-8 pt-6 overflow-x-clip" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { class: "flex items-center justify-start sm:justify-end gap-2 mb-4 flex-wrap" }
const _hoisted_6 = { class: "truncate" }
const _hoisted_7 = { class: "relative flex items-center" }
const _hoisted_8 = { class: "overflow-x-auto p-2 -m-2" }
const _hoisted_9 = { class: "flex items-center justify-between w-full" }
const _hoisted_10 = { class: "flex items-center justify-between w-full" }
const _hoisted_11 = { class: "font-semibold text-gray-900 truncate" }
const _hoisted_12 = { class: "inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-mono font-medium text-gray-700" }
const _hoisted_13 = { class: "text-gray-700" }
const _hoisted_14 = {
  key: 0,
  class: "inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700"
}
const _hoisted_15 = {
  key: 1,
  class: "text-gray-500"
}
const _hoisted_16 = { class: "text-gray-500" }
const _hoisted_17 = { class: "overflow-x-auto p-2 -m-2" }
const _hoisted_18 = { class: "flex items-center justify-between w-full" }
const _hoisted_19 = { class: "flex items-center justify-between w-full" }
const _hoisted_20 = { class: "font-semibold text-gray-900 truncate" }
const _hoisted_21 = { class: "inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-mono font-medium text-gray-700" }
const _hoisted_22 = { class: "text-gray-700" }
const _hoisted_23 = {
  key: 0,
  class: "inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700"
}
const _hoisted_24 = {
  key: 1,
  class: "text-gray-500"
}
const _hoisted_25 = { class: "text-gray-500" }

import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { ChevronDownIcon, ChevronRightIcon, MagnifyingGlassIcon } from '@heroicons/vue/20/solid'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import TablistGrid from '@/components/neue/TablistGrid.vue'
import ReportChart from '@/components/reports/ReportChart.vue'
import { formatDiscount } from '@/helpers/discountFormat'
import { parseDiscountCodeUsageReport } from '@/services/reportParser.service'
import api from '@/store/api'
import { mapType } from '@/store/mappers'

const pageSize = 20

const MANAGE_PATH = '/store/discount-codes/manage'

export default {
  __name: 'DiscountCodeList',
  setup(__props) {

const route = useRoute()
const router = useRouter()
const store = useStore()
const discountCodeCols = [
  { width: 'minmax(80px, 2fr)' }, // Name
  { width: 'minmax(80px, 1fr)' }, // Code
  { width: 'minmax(80px, 1fr)' }, // Discount
  { width: 'minmax(130px, 1fr)' }, // First Time Only
  { width: 'minmax(100px, 1fr)' } // Last Used
]

// Existing state
const codes = computed(() => mapType('discount_code'))
const loading = ref(true)

// Tab state
const activeTab = computed(() => (route.path === MANAGE_PATH ? 'manage' : 'overview'))
const codeTab = ref('active')

// Manage tab pagination
const managePage = ref(0)
const managePrev = ret => {
  managePage.value--
  ret()
}
const manageNext = ret => {
  managePage.value++
  ret()
}

// Inactive codes
const inactiveCodes = ref([])
const inactiveLoading = ref(false)
const inactiveFetched = ref(false)
const inactivePage = ref(0)
const inactivePrev = ret => {
  inactivePage.value--
  ret()
}
const inactiveNext = ret => {
  inactivePage.value++
  ret()
}

// Search
const searchQuery = ref('')

const filteredCodes = computed(() => {
  if (!searchQuery.value) return codes.value
  const q = searchQuery.value.toLowerCase()
  return codes.value.filter(
    c =>
      (c.attributes.discount_code_name || '').toLowerCase().includes(q) ||
      (c.attributes.code || '').toLowerCase().includes(q)
  )
})

const filteredInactiveCodes = computed(() => {
  if (!searchQuery.value) return inactiveCodes.value
  const q = searchQuery.value.toLowerCase()
  return inactiveCodes.value.filter(
    c =>
      (c.attributes.discount_code_name || '').toLowerCase().includes(q) ||
      (c.attributes.code || '').toLowerCase().includes(q)
  )
})

watch(searchQuery, () => {
  managePage.value = 0
  inactivePage.value = 0
})

// Overview tab state
const reportLoading = ref(true)
const chartData = ref({ labels: [], datasets: [] })
const reportTableData = ref(null)
const activeInChartProductIds = ref([])
const productsOptions = ref([])
const legendOptions = computed(() =>
  (productsOptions.value ?? []).map(p => ({ label: p.product_name ?? `Code ${p.id}`, value: p.id }))
)

const fetchInactiveCodes = async () => {
  if (inactiveFetched.value) return
  inactiveLoading.value = true
  try {
    const res = await api.call('GET', 'discount_codes?status=inactive&included=none', {}, { receive: false })
    inactiveCodes.value = res.data?.data || []
    inactiveFetched.value = true
  } catch (error) {
    console.error('Error fetching inactive discount codes:', error)
  } finally {
    inactiveLoading.value = false
  }
}

const switchToInactive = () => {
  codeTab.value = 'inactive'
  fetchInactiveCodes()
}

const fetchReportData = async () => {
  reportLoading.value = true
  try {
    const end = new Date()
    const start = new Date(end)
    start.setDate(end.getDate() - 90)
    const toYMD = d => d.toISOString().slice(0, 10)

    const params = new URLSearchParams({
      beginning_date: toYMD(start),
      ending_date: toYMD(end),
      group_by: 'day'
    })

    const res = await api.call('GET', `data_downloads/reports/discount_code_usage_report?${params.toString()}`)
    const parsed = parseDiscountCodeUsageReport(res)
    chartData.value = parsed.chartData
    reportTableData.value = parsed.tableData
    productsOptions.value = parsed.productsOptions ?? []
    const topTableIds = (parsed.tableData?.rows ?? []).map(r => r.productId)
    activeInChartProductIds.value = topTableIds.slice(0, 5)
  } catch (error) {
    console.error('Error fetching discount code report data:', error)
  } finally {
    reportLoading.value = false
  }
}

onMounted(() => {
  fetchReportData()
  api.call('GET', 'discount_codes?included=none').then(() => {
    loading.value = false
  })
})

return (_ctx, _cache) => {
  const _component_NeueButton = _resolveComponent("NeueButton")
  const _component_router_link = _resolveComponent("router-link")
  const _component_SecondaryTop = _resolveComponent("SecondaryTop")
  const _component_NeueLoading = _resolveComponent("NeueLoading")
  const _component_TablistHeader = _resolveComponent("TablistHeader")
  const _component_TablistRow = _resolveComponent("TablistRow")
  const _component_NeueTablist = _resolveComponent("NeueTablist")
  const _component_NeuePagination = _resolveComponent("NeuePagination")
  const _component_EmptyState = _resolveComponent("EmptyState")
  const _component_NeueWell = _resolveComponent("NeueWell")
  const _component_SecondaryNav = _resolveComponent("SecondaryNav")

  return (_openBlock(), _createBlock(_component_SecondaryNav, null, {
    top: _withCtx(() => [
      _createVNode(_component_SecondaryTop, {
        text: "Discount codes",
        sections: [
          {
            id: 'overview',
            name: 'Overview',
            active: activeTab.value === 'overview',
            action: () => _unref(router).push('/store/discount-codes')
          },
          {
            id: 'manage',
            name: 'Manage discount codes',
            active: activeTab.value === 'manage',
            action: () => _unref(router).push(MANAGE_PATH)
          }
        ]
      }, {
        right: _withCtx(() => [
          _createVNode(_component_router_link, { to: "/store/discount-codes/new" }, {
            default: _withCtx(() => [
              _createVNode(_component_NeueButton, { type: "primary" }, {
                default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                  _createTextVNode("New", -1)
                ]))]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["sections"])
    ]),
    main: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          (activeTab.value === 'overview')
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createVNode(_component_NeueLoading, { loading: reportLoading.value }, {
                  default: _withCtx(() => [
                    _createVNode(ReportChart, {
                      "report-id": "discount-code-usage-overview",
                      title: "Discount code usage",
                      description: "Last 90 days",
                      "empty-state-title": "No usage in the last 90 days",
                      "empty-state-description": "Share your discount codes to start seeing usage data here.",
                      "chart-data": chartData.value,
                      "has-supporting-table": true,
                      "table-data": reportTableData.value,
                      "label-column-title": "Name",
                      "chart-type": "stackedBar",
                      "date-filter": false,
                      "dropdown-filter": false,
                      loading: reportLoading.value,
                      "table-hover-enabled": false,
                      "view-report-link": "/discount-code-usage",
                      "active-in-chart-product-ids": activeInChartProductIds.value,
                      "dropdown-options3": legendOptions.value,
                      onActiveChartProductsChange: _cache[0] || (_cache[0] = ids => (activeInChartProductIds.value = ids))
                    }, null, 8, ["chart-data", "table-data", "loading", "active-in-chart-product-ids", "dropdown-options3"])
                  ]),
                  _: 1
                }, 8, ["loading"])
              ]))
            : _createCommentVNode("", true),
          (activeTab.value === 'manage')
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(_unref(Popover), {
                    as: "div",
                    class: "relative"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(PopoverButton), { class: "inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-brand-500" }, {
                        default: _withCtx(() => [
                          _createElementVNode("span", _hoisted_6, _toDisplayString(codeTab.value === 'active' ? 'Active' : 'Past / Inactive'), 1),
                          _createVNode(_unref(ChevronDownIcon), {
                            class: "h-4 w-4 text-gray-500 shrink-0",
                            "aria-hidden": "true"
                          })
                        ]),
                        _: 1
                      }),
                      _createVNode(_Transition, {
                        "enter-active-class": "transition ease-out duration-100",
                        "enter-from-class": "transform opacity-0 scale-95",
                        "enter-to-class": "transform opacity-100 scale-100",
                        "leave-active-class": "transition ease-in duration-75",
                        "leave-from-class": "transform opacity-100 scale-100",
                        "leave-to-class": "transform opacity-0 scale-95"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(PopoverPanel), { class: "absolute right-0 z-20 mt-2 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 px-4 py-1" }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(PopoverButton), {
                                class: "text-sm font-medium whitespace-nowrap text-gray-900 cursor-pointer my-3 block",
                                onClick: _cache[1] || (_cache[1] = $event => (codeTab.value = 'active'))
                              }, {
                                default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                                  _createTextVNode(" Active ", -1)
                                ]))]),
                                _: 1
                              }),
                              _createVNode(_unref(PopoverButton), {
                                class: "text-sm font-medium whitespace-nowrap text-gray-900 cursor-pointer my-3 block",
                                onClick: switchToInactive
                              }, {
                                default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                                  _createTextVNode(" Past / Inactive ", -1)
                                ]))]),
                                _: 1
                              })
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createElementVNode("div", _hoisted_7, [
                    _createVNode(_unref(MagnifyingGlassIcon), { class: "pointer-events-none absolute left-3 h-4 w-4 text-gray-500" }),
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = $event => ((searchQuery).value = $event)),
                      type: "text",
                      class: "h-[34px] w-56 pl-9 pr-3 rounded-full border border-gray-300 bg-white text-sm text-gray-700 placeholder-gray-500 shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500",
                      placeholder: "Search discount codes..."
                    }, null, 512), [
                      [_vModelText, searchQuery.value]
                    ])
                  ])
                ]),
                (codeTab.value === 'active')
                  ? (_openBlock(), _createBlock(_component_NeueLoading, {
                      key: 0,
                      loading: loading.value
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_8, [
                          (filteredCodes.value.length)
                            ? (_openBlock(), _createBlock(_component_NeueTablist, { key: 0 }, {
                                head: _withCtx(() => [
                                  _createVNode(_component_TablistHeader, null, {
                                    default: _withCtx(() => [
                                      _createElementVNode("div", _hoisted_9, [
                                        _createVNode(TablistGrid, {
                                          columns: discountCodeCols,
                                          header: "",
                                          class: "flex-1"
                                        }, {
                                          default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                                            _createElementVNode("div", null, "Name", -1),
                                            _createElementVNode("div", null, "Code", -1),
                                            _createElementVNode("div", null, "Discount", -1),
                                            _createElementVNode("div", null, "First Time Only", -1),
                                            _createElementVNode("div", null, "Last Used", -1)
                                          ]))]),
                                          _: 1
                                        }),
                                        _cache[7] || (_cache[7] = _createElementVNode("div", { class: "h-5 w-5 flex-shrink-0" }, null, -1))
                                      ])
                                    ]),
                                    _: 1
                                  })
                                ]),
                                default: _withCtx(() => [
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredCodes.value.slice(managePage.value * pageSize, (1 + managePage.value) * pageSize), (code) => {
                                    return (_openBlock(), _createBlock(_component_TablistRow, {
                                      key: code.id,
                                      class: "cursor-pointer",
                                      onClick: $event => (_unref(router).push('/store/discount-codes/' + code.id))
                                    }, {
                                      default: _withCtx(() => [
                                        _createElementVNode("div", _hoisted_10, [
                                          _createVNode(TablistGrid, {
                                            columns: discountCodeCols,
                                            class: "flex-1 items-center"
                                          }, {
                                            default: _withCtx(() => [
                                              _createElementVNode("div", _hoisted_11, _toDisplayString(code.attributes.discount_code_name), 1),
                                              _createElementVNode("div", null, [
                                                _createElementVNode("span", _hoisted_12, _toDisplayString(code.attributes.code), 1)
                                              ]),
                                              _createElementVNode("div", _hoisted_13, _toDisplayString(_unref(formatDiscount)(code)), 1),
                                              _createElementVNode("div", null, [
                                                (code.attributes.first_time_customer_only)
                                                  ? (_openBlock(), _createElementBlock("span", _hoisted_14, " Yes "))
                                                  : (_openBlock(), _createElementBlock("span", _hoisted_15, "No"))
                                              ]),
                                              _createElementVNode("div", _hoisted_16, _toDisplayString(code.attributes.last_used || 'Never'), 1)
                                            ]),
                                            _: 2
                                          }, 1024),
                                          _createVNode(_unref(ChevronRightIcon), { class: "h-5 w-5 text-gray-400 flex-shrink-0" })
                                        ])
                                      ]),
                                      _: 2
                                    }, 1032, ["onClick"]))
                                  }), 128))
                                ]),
                                _: 1
                              }))
                            : _createCommentVNode("", true)
                        ]),
                        (filteredCodes.value.length > pageSize)
                          ? (_openBlock(), _createBlock(_component_NeuePagination, {
                              key: 0,
                              size: filteredCodes.value.length,
                              from: 1 + managePage.value * pageSize,
                              to: Math.min((1 + managePage.value) * pageSize, filteredCodes.value.length),
                              onPrev: managePrev,
                              onNext: manageNext
                            }, null, 8, ["size", "from", "to"]))
                          : _createCommentVNode("", true),
                        (!filteredCodes.value.length)
                          ? (_openBlock(), _createBlock(_component_NeueWell, {
                              key: 1,
                              class: "text-center"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_EmptyState, { plural: "discount codes" })
                              ]),
                              _: 1
                            }))
                          : _createCommentVNode("", true)
                      ]),
                      _: 1
                    }, 8, ["loading"]))
                  : _createCommentVNode("", true),
                (codeTab.value === 'inactive')
                  ? (_openBlock(), _createBlock(_component_NeueLoading, {
                      key: 1,
                      loading: inactiveLoading.value
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_17, [
                          (filteredInactiveCodes.value.length)
                            ? (_openBlock(), _createBlock(_component_NeueTablist, { key: 0 }, {
                                head: _withCtx(() => [
                                  _createVNode(_component_TablistHeader, null, {
                                    default: _withCtx(() => [
                                      _createElementVNode("div", _hoisted_18, [
                                        _createVNode(TablistGrid, {
                                          columns: discountCodeCols,
                                          header: "",
                                          class: "flex-1"
                                        }, {
                                          default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                                            _createElementVNode("div", null, "Name", -1),
                                            _createElementVNode("div", null, "Code", -1),
                                            _createElementVNode("div", null, "Discount", -1),
                                            _createElementVNode("div", null, "First Time Only", -1),
                                            _createElementVNode("div", null, "Last Used", -1)
                                          ]))]),
                                          _: 1
                                        }),
                                        _cache[9] || (_cache[9] = _createElementVNode("div", { class: "h-5 w-5 flex-shrink-0" }, null, -1))
                                      ])
                                    ]),
                                    _: 1
                                  })
                                ]),
                                default: _withCtx(() => [
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredInactiveCodes.value.slice(inactivePage.value * pageSize, (1 + inactivePage.value) * pageSize), (code) => {
                                    return (_openBlock(), _createBlock(_component_TablistRow, {
                                      key: code.id,
                                      class: "cursor-pointer",
                                      onClick: $event => (_unref(router).push('/store/discount-codes/' + code.id))
                                    }, {
                                      default: _withCtx(() => [
                                        _createElementVNode("div", _hoisted_19, [
                                          _createVNode(TablistGrid, {
                                            columns: discountCodeCols,
                                            class: "flex-1 items-center"
                                          }, {
                                            default: _withCtx(() => [
                                              _createElementVNode("div", _hoisted_20, _toDisplayString(code.attributes.discount_code_name), 1),
                                              _createElementVNode("div", null, [
                                                _createElementVNode("span", _hoisted_21, _toDisplayString(code.attributes.code), 1)
                                              ]),
                                              _createElementVNode("div", _hoisted_22, _toDisplayString(_unref(formatDiscount)(code)), 1),
                                              _createElementVNode("div", null, [
                                                (code.attributes.first_time_customer_only)
                                                  ? (_openBlock(), _createElementBlock("span", _hoisted_23, " Yes "))
                                                  : (_openBlock(), _createElementBlock("span", _hoisted_24, "No"))
                                              ]),
                                              _createElementVNode("div", _hoisted_25, _toDisplayString(code.attributes.last_used || 'Never'), 1)
                                            ]),
                                            _: 2
                                          }, 1024),
                                          _createVNode(_unref(ChevronRightIcon), { class: "h-5 w-5 text-gray-400 flex-shrink-0" })
                                        ])
                                      ]),
                                      _: 2
                                    }, 1032, ["onClick"]))
                                  }), 128))
                                ]),
                                _: 1
                              }))
                            : _createCommentVNode("", true)
                        ]),
                        (filteredInactiveCodes.value.length > pageSize)
                          ? (_openBlock(), _createBlock(_component_NeuePagination, {
                              key: 0,
                              size: filteredInactiveCodes.value.length,
                              from: 1 + inactivePage.value * pageSize,
                              to: Math.min((1 + inactivePage.value) * pageSize, filteredInactiveCodes.value.length),
                              onPrev: inactivePrev,
                              onNext: inactiveNext
                            }, null, 8, ["size", "from", "to"]))
                          : _createCommentVNode("", true),
                        (!filteredInactiveCodes.value.length)
                          ? (_openBlock(), _createBlock(_component_NeueWell, {
                              key: 1,
                              class: "text-center"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_EmptyState, { plural: "inactive discount codes" })
                              ]),
                              _: 1
                            }))
                          : _createCommentVNode("", true)
                      ]),
                      _: 1
                    }, 8, ["loading"]))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true)
        ])
      ])
    ]),
    _: 1
  }))
}
}

}